import {
  CheckOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  LinkOutlined,
  WarningFilled
} from "@ant-design/icons"
import { Empty, Modal, Tooltip, Typography } from "antd"
import dayjsNow, { type Dayjs } from "dayjs"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { ActionTooltip } from "../../../../../../../components/ActionTooltip.tsx"
import { useDateFormat } from "../../../../../../../hooks/use-date-format.ts"
import { MilestoneStatusTag } from "./MilestoneStatusTag.tsx"
import {
  MILESTONE_STATUS_ORDER,
  MILESTONE_STATUSES,
  MILESTONE_STATUSES_MAP
} from "../../../../constants/milestone-statuses.ts"
import {
  TYPE_INFO,
  sourceSideFor,
  targetSideFor
} from "../../../../constants/milestone-dependency-relationship.ts"
import { getMilestoneColor } from "../../../../constants/milestone-palette.ts"
import {
  calculateEffectiveSchedule,
  type EffectiveScheduleEntry
} from "../../../../utils/milestone-effective-schedule.ts"
import { type MilestoneDependency } from "../../../../../types/project-milestone-dependency.types.ts"
import { type ProjectMilestone } from "../../../../../types/project-phases.types.tsx"

const { Text } = Typography

interface GanttSizes {
  minDayWidth: number
  maxDayWidth: number
  rowHeight: number
  headerHeight: number
  monthHeaderHeight: number
  /** Its own row between the month header and the week/date labels, so the
   * Today badge never shares vertical space with a date label it could
   * cover. */
  todayRowHeight: number
  barHeight: number
  stub: number
  milestoneColWidth: number
  dependenciesColWidth: number
  statusColWidth: number
}

// The expanded workspace isn't the compact chart scaled up — it gets a wider
// day-width ceiling, taller rows and more room for names, so the same data
// reads with more room rather than just being blown up.
const COMPACT_SIZES: GanttSizes = {
  minDayWidth: 26,
  maxDayWidth: 60,
  rowHeight: 44,
  headerHeight: 26,
  monthHeaderHeight: 24,
  todayRowHeight: 20,
  barHeight: 26,
  stub: 12,
  milestoneColWidth: 170,
  dependenciesColWidth: 95,
  statusColWidth: 88
}

const EXPANDED_SIZES: GanttSizes = {
  minDayWidth: 36,
  maxDayWidth: 90,
  rowHeight: 52,
  headerHeight: 28,
  monthHeaderHeight: 26,
  todayRowHeight: 22,
  barHeight: 32,
  stub: 16,
  milestoneColWidth: 230,
  dependenciesColWidth: 120,
  statusColWidth: 110
}

// Beyond roughly six months, weekly columns become too many tiny slivers to
// read — the timeline reads as one label per calendar month instead.
const MONTH_RESOLUTION_THRESHOLD_DAYS = 180

const TEAL = "var(--ant-color-primary)"

interface ProjectViewMilestonesGanttProps {
  milestones: ProjectMilestone[]
  dependencies: MilestoneDependency[]
  projectName?: string
}

interface ScheduledEntry {
  milestone: ProjectMilestone
  rowIndex: number
  entry: EffectiveScheduleEntry
}

interface ScheduledBar {
  milestone: ProjectMilestone
  rowIndex: number
  start: Dayjs
  end: Dayjs
  left: number
  width: number
  durationDays: number
  isShifted: boolean
  shiftReason: EffectiveScheduleEntry["shiftReason"]
  /** Pixel X of the actual finish, when one is recorded — a separate fact
   * from the planned bar, never a substitute for it. */
  actualEndOffset: number | null
}

interface RelatedDependencyRow {
  dependency: MilestoneDependency
  otherName: string
  relation: string
}

interface MonthGroup {
  offsetDays: number
  widthDays: number
  label: string
}

interface GanttLayout {
  bars: ScheduledBar[]
  barById: Map<string, ScheduledBar>
  weeks: { offsetDays: number; label: string }[]
  monthGroups: MonthGroup[]
  resolution: "week" | "month"
  todayOffset: number | null
  chartWidth: number
  dayWidth: number
  connectors: { dep: MilestoneDependency; d: string; isHighlighted: boolean }[]
}

const anchorX = (bar: ScheduledBar, side: "start" | "end") =>
  side === "start" ? bar.left : bar.left + bar.width

const rowY = (rowIndex: number, rowHeight: number) => rowIndex * rowHeight + rowHeight / 2

// "Aug 23 – 29" within a month, "Aug 30 – Sep 5" across a month boundary —
// the month/year now lives one level up in its own header row, so this stays
// a bare date range.
const formatWeekRange = (start: Dayjs, end: Dayjs) =>
  start.month() === end.month()
    ? `${start.format("MMM D")} – ${end.format("D")}`
    : `${start.format("MMM D")} – ${end.format("MMM D")}`

function buildMonthGroups(range: { start: Dayjs; end: Dayjs }): MonthGroup[] {
  const groups: MonthGroup[] = []
  let cursor = range.start.startOf("month")
  while (cursor.isBefore(range.end)) {
    const segmentStart = cursor.isBefore(range.start) ? range.start : cursor
    const nextMonth = cursor.add(1, "month")
    const segmentEndExclusive = nextMonth.isAfter(range.end) ? range.end.add(1, "day") : nextMonth
    const offsetDays = segmentStart.diff(range.start, "day")
    const widthDays = Math.max(segmentEndExclusive.diff(segmentStart, "day"), 1)
    groups.push({ offsetDays, widthDays, label: cursor.format("MMMM YYYY") })
    cursor = nextMonth
  }
  return groups
}

// Pixel layout is the only thing that depends on which surface (inline vs
// expanded) is rendering, and on how much horizontal space that surface
// actually has — the schedule itself (dates, shifts, reasons) is computed
// once and shared by both.
function buildLayout(
  scheduled: ScheduledEntry[],
  range: { start: Dayjs; end: Dayjs } | null,
  dependencies: MilestoneDependency[],
  connected: Set<string> | null,
  sizes: GanttSizes,
  containerWidth: number
): GanttLayout {
  if (!range) {
    return {
      bars: [],
      barById: new Map(),
      weeks: [],
      monthGroups: [],
      resolution: "week",
      todayOffset: null,
      chartWidth: 0,
      dayWidth: sizes.minDayWidth,
      connectors: []
    }
  }

  const totalDays = Math.max(range.end.diff(range.start, "day") + 1, 1)
  const resolution: "week" | "month" =
    totalDays > MONTH_RESOLUTION_THRESHOLD_DAYS ? "month" : "week"

  // Auto-fit: stretch each day to use the surface's real available width
  // instead of leaving a wide blank strip for a short project, but clamp so
  // a two-day project doesn't get absurd 300px-wide columns and a two-year
  // one doesn't get unreadably thin slivers (it scrolls instead).
  const fitted = containerWidth > 0 ? containerWidth / totalDays : sizes.minDayWidth
  const dayWidth = Math.min(Math.max(fitted, sizes.minDayWidth), sizes.maxDayWidth)
  const chartWidth = Math.max(totalDays * dayWidth, containerWidth || 0)

  const bars: ScheduledBar[] = scheduled.map(({ milestone, rowIndex, entry }) => {
    const left = entry.start.diff(range.start, "day") * dayWidth
    const durationDays = Math.max(entry.end.diff(entry.start, "day") + 1, 1)
    return {
      milestone,
      rowIndex,
      start: entry.start,
      end: entry.end,
      left,
      width: durationDays * dayWidth,
      durationDays,
      isShifted: entry.isShifted,
      shiftReason: entry.shiftReason,
      actualEndOffset: entry.actualEnd
        ? (entry.actualEnd.diff(range.start, "day") + 1) * dayWidth
        : null
    }
  })

  const barById = new Map(bars.map((bar) => [bar.milestone.id, bar]))

  const today = dayjsNow().startOf("day")
  const todayOffset =
    today.isBefore(range.start) || today.isAfter(range.end)
      ? null
      : today.diff(range.start, "day") * dayWidth

  const weeks: { offsetDays: number; label: string }[] = []
  if (resolution === "week") {
    let cursor = range.start.startOf("week")
    while (cursor.isBefore(range.end)) {
      const offsetDays = cursor.diff(range.start, "day")
      if (offsetDays >= 0) {
        weeks.push({ offsetDays, label: formatWeekRange(cursor, cursor.add(6, "day")) })
      }
      cursor = cursor.add(1, "week")
    }
  }

  const monthGroups = buildMonthGroups(range)

  const connectors = dependencies
    .map((dep) => {
      const source = barById.get(dep.source_milestone)
      const target = barById.get(dep.target_milestone)
      if (!source || !target) return null

      const sourceSide = sourceSideFor(dep.type)
      const targetSide = targetSideFor(dep.type)

      const sx = anchorX(source, sourceSide)
      const sy = rowY(source.rowIndex, sizes.rowHeight)
      const tx = anchorX(target, targetSide)
      const ty = rowY(target.rowIndex, sizes.rowHeight)

      const lagPx = Math.max(dep.lag_days, 0) * dayWidth
      const stubDir = sourceSide === "end" ? 1 : -1
      const stubX = sx + stubDir * (sizes.stub + lagPx)

      const isHighlighted =
        !!connected && connected.has(dep.source_milestone) && connected.has(dep.target_milestone)

      return { dep, d: `M ${sx} ${sy} H ${stubX} V ${ty} H ${tx}`, isHighlighted }
    })
    .filter((connector): connector is NonNullable<typeof connector> => !!connector)

  return { bars, barById, weeks, monthGroups, resolution, todayOffset, chartWidth, dayWidth, connectors }
}

export function ProjectViewMilestonesGantt({
  milestones,
  dependencies,
  projectName
}: ProjectViewMilestonesGanttProps) {
  const { parseDate } = useDateFormat()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [isExpanded, setIsExpanded] = useState(false)

  const nameById = useMemo(() => {
    const map = new Map<string, string>()
    milestones.forEach((m) => map.set(m.id, m.name))
    return map
  }, [milestones])

  const colorById = useMemo(() => {
    const map = new Map<string, string>()
    milestones.forEach((m, index) => map.set(m.id, getMilestoneColor(index)))
    return map
  }, [milestones])

  const dependencyCountByMilestone = useMemo(() => {
    const map = new Map<string, number>()
    dependencies.forEach((d) => {
      map.set(d.source_milestone, (map.get(d.source_milestone) || 0) + 1)
      map.set(d.target_milestone, (map.get(d.target_milestone) || 0) + 1)
    })
    return map
  }, [dependencies])

  const directDependenciesFor = (milestoneId: string) =>
    dependencies
      .filter((d) => d.source_milestone === milestoneId || d.target_milestone === milestoneId)
      .map((d) => {
        const isSource = d.source_milestone === milestoneId
        return {
          dependency: d,
          otherName: nameById.get(isSource ? d.target_milestone : d.source_milestone) || "—",
          relation: isSource ? "Required for" : "Depends on"
        }
      })

  // The single scheduling engine: a milestone's dates here are never its raw
  // Planned Start/End if a dependency requires later ones — the Table reads
  // from the exact same function, so the two views can never disagree. This
  // one calculation is shared by both the inline and expanded surfaces below.
  const effectiveSchedule = useMemo(
    () => calculateEffectiveSchedule({ milestones, dependencies, parseDate }),
    [milestones, dependencies, parseDate]
  )

  const scheduled = useMemo<ScheduledEntry[]>(
    () =>
      milestones
        .map((milestone, rowIndex) => {
          const entry = effectiveSchedule.get(milestone.id)
          return entry ? { milestone, rowIndex, entry } : null
        })
        .filter((row): row is ScheduledEntry => !!row),
    [milestones, effectiveSchedule]
  )

  const range = useMemo(() => {
    if (!scheduled.length) return null
    const starts = scheduled.map(({ entry }) => entry.start)
    const ends = scheduled.map(({ entry }) => entry.end)
    const start = starts.reduce((a, b) => (b.isBefore(a) ? b : a))
    const end = ends.reduce((a, b) => (b.isAfter(a) ? b : a))
    // ~7% padding each side rather than a fixed number of days, so a short
    // project doesn't get padded out of proportion and a long one doesn't
    // lose its padding to rounding.
    const spanDays = end.diff(start, "day") + 1
    const paddingDays = Math.max(2, Math.round(spanDays * 0.07))
    const paddedStart = start.subtract(paddingDays, "day")
    const paddedEnd = end.add(paddingDays, "day")
    // Today is date-based only, independent of the schedule — if it falls
    // outside the padded schedule range, expand to include it rather than
    // leaving the indicator unable to render at all.
    const today = dayjsNow().startOf("day")
    return {
      start: today.isBefore(paddedStart) ? today : paddedStart,
      end: today.isAfter(paddedEnd) ? today : paddedEnd
    }
  }, [scheduled])

  // Everything reachable from the selected milestone through the dependency
  // graph, in either direction — the "chain" to highlight, not just its
  // immediate neighbours. Shared by both surfaces since selection is one
  // piece of state on this component, not duplicated per surface.
  const connected = useMemo(() => {
    if (!selectedId) return null
    const adjacency = new Map<string, string[]>()
    dependencies.forEach((dep) => {
      adjacency.set(dep.source_milestone, [
        ...(adjacency.get(dep.source_milestone) || []),
        dep.target_milestone
      ])
      adjacency.set(dep.target_milestone, [
        ...(adjacency.get(dep.target_milestone) || []),
        dep.source_milestone
      ])
    })
    const visited = new Set<string>([selectedId])
    const queue = [selectedId]
    while (queue.length) {
      const current = queue.shift() as string
      for (const next of adjacency.get(current) || []) {
        if (!visited.has(next)) {
          visited.add(next)
          queue.push(next)
        }
      }
    }
    return visited
  }, [selectedId, dependencies])

  const clearSelection = () => setSelectedId(null)
  const toggleSelect = (id: string, e?: React.SyntheticEvent) => {
    e?.stopPropagation()
    setSelectedId((current) => (current === id ? null : id))
  }

  const selectedMilestone = selectedId
    ? milestones.find((m) => m.id === selectedId) || null
    : null

  if (!range) {
    return (
      <div className="py-10">
        <Empty description="Set Planned Start and Planned End dates on your milestones to see them on the timeline." />
      </div>
    )
  }

  const sharedProps = {
    milestones,
    scheduled,
    range,
    dependencies,
    connected,
    selectedId,
    colorById,
    dependencyCountByMilestone,
    directDependenciesFor,
    nameById,
    parseDate,
    onToggleSelect: toggleSelect,
    onClearSelection: clearSelection
  }

  return (
    <div>
      <div className="flex items-center justify-end mb-1.5">
        <button
          type="button"
          onClick={() => setIsExpanded(true)}
          className="flex items-center gap-1.5 text-xs px-2 py-1 rounded transition-colors"
          style={{ color: TEAL, background: "transparent" }}
        >
          <FullscreenOutlined style={{ fontSize: 12 }} />
          Expand Gantt
        </button>
      </div>

      <GanttChartBody {...sharedProps} sizes={COMPACT_SIZES} />
      <MilestonesGanttLegend />
      {selectedMilestone && (
        <MilestoneDetailPanel
          milestone={selectedMilestone}
          related={directDependenciesFor(selectedMilestone.id)}
          effectiveEntry={effectiveSchedule.get(selectedMilestone.id) || null}
          nameById={nameById}
          parseDate={parseDate}
        />
      )}

      {isExpanded && (
        <Modal
          open
          onCancel={() => setIsExpanded(false)}
          footer={null}
          width="92vw"
          style={{ top: "4vh" }}
          styles={{ body: { maxHeight: "88vh", overflowY: "auto" } }}
          title={
            <div className="flex items-center justify-between pr-6">
              <div>
                <div style={{ fontSize: 16, fontWeight: 600 }}>Gantt Schedule</div>
                {projectName && (
                  <Text type="secondary" style={{ fontSize: 13, fontWeight: 400 }}>
                    {projectName}
                  </Text>
                )}
              </div>
              <button
                type="button"
                onClick={() => setIsExpanded(false)}
                className="flex items-center gap-1.5 text-xs px-2 py-1 rounded"
                style={{ color: TEAL }}
              >
                <FullscreenExitOutlined style={{ fontSize: 12 }} />
                Collapse
              </button>
            </div>
          }
        >
          <GanttChartBody {...sharedProps} sizes={EXPANDED_SIZES} />
          <MilestonesGanttLegend />
          {selectedMilestone && (
            <MilestoneDetailPanel
              milestone={selectedMilestone}
              related={directDependenciesFor(selectedMilestone.id)}
              effectiveEntry={effectiveSchedule.get(selectedMilestone.id) || null}
              nameById={nameById}
              parseDate={parseDate}
            />
          )}
        </Modal>
      )}
    </div>
  )
}

function GanttChartBody({
  milestones,
  scheduled,
  range,
  dependencies,
  sizes,
  connected,
  selectedId,
  colorById,
  dependencyCountByMilestone,
  directDependenciesFor,
  nameById,
  parseDate,
  onToggleSelect,
  onClearSelection
}: {
  milestones: ProjectMilestone[]
  scheduled: ScheduledEntry[]
  range: { start: Dayjs; end: Dayjs }
  dependencies: MilestoneDependency[]
  sizes: GanttSizes
  connected: Set<string> | null
  selectedId: string | null
  colorById: Map<string, string>
  dependencyCountByMilestone: Map<string, number>
  directDependenciesFor: (id: string) => RelatedDependencyRow[]
  nameById: Map<string, string>
  parseDate: (value: unknown) => Dayjs | null
  onToggleSelect: (id: string, e?: React.SyntheticEvent) => void
  onClearSelection: () => void
}) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState(0)

  useEffect(() => {
    const el = scrollRef.current
    if (!el || typeof ResizeObserver === "undefined") return
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width
      if (width) setContainerWidth(width)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const layout = useMemo(
    () => buildLayout(scheduled, range, dependencies, connected, sizes, containerWidth),
    [scheduled, range, dependencies, connected, sizes, containerWidth]
  )

  const {
    bars,
    barById,
    weeks,
    monthGroups,
    resolution,
    todayOffset,
    chartWidth,
    dayWidth,
    connectors
  } = layout
  const leftPanelWidth = sizes.milestoneColWidth + sizes.dependenciesColWidth + sizes.statusColWidth
  const headerHeight = sizes.monthHeaderHeight + sizes.todayRowHeight + sizes.headerHeight
  const bodyHeight = milestones.length * sizes.rowHeight

  return (
    <div
      className="flex rounded-lg overflow-hidden"
      style={{ border: "1px solid #eef0f2" }}
      onClick={onClearSelection}
    >
      <div
        className="flex-shrink-0 bg-white flex"
        style={{ width: leftPanelWidth, borderRight: "1px solid #eef0f2" }}
      >
        <div style={{ width: sizes.milestoneColWidth }}>
          <div
            className="flex items-end px-3 pb-1 text-xs font-medium uppercase tracking-wide text-gray-400"
            style={{ height: headerHeight, borderBottom: "1px solid #eef0f2" }}
          >
            Milestone
          </div>
          {milestones.map((milestone, index) => {
            const isDimmed = !!connected && !connected.has(milestone.id)
            const isSelected = selectedId === milestone.id
            return (
              <div
                key={milestone.id}
                className="flex items-center gap-2 px-3 cursor-pointer transition-colors"
                style={{
                  height: sizes.rowHeight,
                  opacity: isDimmed ? 0.4 : 1,
                  borderBottom: "1px solid #f5f6f7",
                  backgroundColor: isSelected ? "#f0f7f6" : "transparent"
                }}
                onClick={(e) => onToggleSelect(milestone.id, e)}
              >
                <span
                  className="inline-block w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: colorById.get(milestone.id) }}
                />
                <Text type="secondary" className="text-xs flex-shrink-0">
                  {index + 1}.
                </Text>
                <span className="truncate text-sm" title={milestone.name}>
                  {milestone.name}
                </span>
              </div>
            )
          })}
        </div>

        <div style={{ width: sizes.dependenciesColWidth, borderLeft: "1px solid #f5f6f7" }}>
          <div
            className="flex items-end justify-center px-2 pb-1 text-xs font-medium uppercase tracking-wide text-gray-400"
            style={{ height: headerHeight, borderBottom: "1px solid #eef0f2" }}
          >
            Dependencies
          </div>
          {milestones.map((milestone) => {
            const isDimmed = !!connected && !connected.has(milestone.id)
            const count = dependencyCountByMilestone.get(milestone.id) || 0
            return (
              <div
                key={milestone.id}
                className="flex items-center justify-center cursor-pointer"
                style={{
                  height: sizes.rowHeight,
                  opacity: isDimmed ? 0.4 : 1,
                  borderBottom: "1px solid #f5f6f7"
                }}
                onClick={(e) => onToggleSelect(milestone.id, e)}
              >
                {count ? (
                  <Text style={{ fontSize: 12, color: TEAL }}>
                    <LinkOutlined /> {count}
                  </Text>
                ) : (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    —
                  </Text>
                )}
              </div>
            )
          })}
        </div>

        <div style={{ width: sizes.statusColWidth, borderLeft: "1px solid #f5f6f7" }}>
          <div
            className="flex items-end justify-center px-2 pb-1 text-xs font-medium uppercase tracking-wide text-gray-400"
            style={{ height: headerHeight, borderBottom: "1px solid #eef0f2" }}
          >
            Status
          </div>
          {milestones.map((milestone) => {
            const isDimmed = !!connected && !connected.has(milestone.id)
            return (
              <div
                key={milestone.id}
                className="flex items-center justify-center cursor-pointer"
                style={{
                  height: sizes.rowHeight,
                  opacity: isDimmed ? 0.4 : 1,
                  borderBottom: "1px solid #f5f6f7"
                }}
                onClick={(e) => onToggleSelect(milestone.id, e)}
              >
                <MilestoneStatusTag
                  status={milestone.status}
                  label={milestone.status_display}
                  style={{ margin: 0, fontSize: 11 }}
                />
              </div>
            )
          })}
        </div>
      </div>

      <div className="overflow-x-auto flex-1" ref={scrollRef}>
        <div style={{ width: chartWidth, position: "relative" }}>
          <div
            className="relative"
            style={{ height: headerHeight, borderBottom: "1px solid #eef0f2" }}
          >
            {/* Month/year super-header — one label per calendar month, never
                repeated per column, so a long timeline still reads clearly. */}
            <div className="relative" style={{ height: sizes.monthHeaderHeight }}>
              {monthGroups.map((group) => (
                <div
                  key={group.label}
                  className="absolute top-0 h-full flex items-center text-xs font-semibold text-gray-600"
                  style={{
                    left: group.offsetDays * dayWidth + 6,
                    width: group.widthDays * dayWidth - 6
                  }}
                >
                  <span className="truncate">{group.label}</span>
                </div>
              ))}
            </div>
            {/* Today gets its own row, entirely separate from the date
                labels below it — a badge that happened to land over "Aug 30"
                or "Sep 6 – 12" would make that label unreadable, so the two
                can never share vertical space no matter where today falls. */}
            <div
              data-testid="gantt-today-row"
              className="relative"
              style={{ height: sizes.todayRowHeight, borderTop: "1px solid #f2f3f4" }}
            >
              {todayOffset !== null && (
                <div
                  className="absolute bottom-0 flex flex-col items-center"
                  style={{ left: todayOffset, transform: "translateX(-50%)", zIndex: 2 }}
                >
                  <span
                    className="text-[10px] font-medium leading-4 px-1.5 rounded whitespace-nowrap"
                    style={{ color: "#fff", backgroundColor: TEAL }}
                  >
                    Today
                  </span>
                  <span
                    style={{
                      width: 0,
                      height: 0,
                      borderLeft: "3px solid transparent",
                      borderRight: "3px solid transparent",
                      borderTop: `3px solid ${TEAL}`
                    }}
                  />
                </div>
              )}
            </div>
            {resolution === "week" && (
              <div
                data-testid="gantt-date-row"
                className="relative"
                style={{ height: sizes.headerHeight, borderTop: "1px solid #f2f3f4" }}
              >
                {weeks.map((week) => (
                  <div
                    key={week.offsetDays}
                    className="absolute top-0 h-full flex items-center text-[11px] text-gray-400"
                    style={{ left: week.offsetDays * dayWidth + 6 }}
                  >
                    {week.label}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="relative" style={{ height: bodyHeight }}>
            {resolution === "week" &&
              weeks.map((week) => (
                <div
                  key={week.offsetDays}
                  className="absolute top-0 bottom-0"
                  style={{ left: week.offsetDays * dayWidth, borderLeft: "1px solid #f2f3f4" }}
                />
              ))}

            {/* A visibly stronger separator at each month boundary — the
                subtle week gridlines fade into the background, the month
                lines don't. */}
            {monthGroups.map(
              (group, index) =>
                index > 0 && (
                  <div
                    key={`month-sep-${group.label}`}
                    className="absolute top-0 bottom-0"
                    style={{ left: group.offsetDays * dayWidth, borderLeft: "1px solid #dfe3e7" }}
                  />
                )
            )}

            {todayOffset !== null && (
              <div
                className="absolute top-0 bottom-0"
                style={{ left: todayOffset, borderLeft: `1px dashed ${TEAL}`, opacity: 0.6 }}
              />
            )}

            <svg
              data-testid="dependency-connectors"
              className="absolute top-0 left-0"
              width={chartWidth}
              height={bodyHeight}
              style={{ pointerEvents: "none" }}
            >
              <defs>
                <marker
                  id="gantt-dependency-arrow"
                  markerWidth={7}
                  markerHeight={7}
                  refX={5.5}
                  refY={3.5}
                  orient="auto"
                >
                  <path d="M0,0 L7,3.5 L0,7 Z" fill="#94a3b8" />
                </marker>
                <marker
                  id="gantt-dependency-arrow-active"
                  markerWidth={7}
                  markerHeight={7}
                  refX={5.5}
                  refY={3.5}
                  orient="auto"
                >
                  <path d="M0,0 L7,3.5 L0,7 Z" fill={TEAL} />
                </marker>
              </defs>
              {connectors.map(({ dep, d, isHighlighted }) => {
                const dimmed = !!connected && !isHighlighted
                return (
                  <g key={dep.id} opacity={dimmed ? 0.18 : 1}>
                    <path
                      d={d}
                      fill="none"
                      stroke={isHighlighted ? TEAL : "#94a3b8"}
                      strokeWidth={isHighlighted ? 2 : 1.5}
                      markerEnd={`url(#gantt-dependency-arrow${isHighlighted ? "-active" : ""})`}
                    />
                  </g>
                )
              })}
            </svg>

            {bars.map((bar) => {
              const isSelected = selectedId === bar.milestone.id
              const isDimmed = !!connected && !connected.has(bar.milestone.id)
              const isCompleted = bar.milestone.status === MILESTONE_STATUSES.COMPLETED
              const related = directDependenciesFor(bar.milestone.id)
              const color = colorById.get(bar.milestone.id) || "#90A4AE"
              const rawStart = parseDate(bar.milestone.start_date)
              const rawEnd = parseDate(bar.milestone.end_date)
              const actualEndDate = parseDate(bar.milestone.actual_end_date)
              // A one-day bar at most day-widths is nearly invisible as a
              // rectangle — a small diamond marker reads far more clearly.
              const isSingleDay = bar.durationDays === 1
              const rowTop = bar.rowIndex * sizes.rowHeight + sizes.rowHeight / 2

              return (
                <React.Fragment key={bar.milestone.id}>
                  <Tooltip
                    title={
                      <MilestoneBarTooltipContent
                        name={bar.milestone.name}
                        start={bar.start}
                        end={bar.end}
                        durationDays={bar.durationDays}
                        statusLabel={bar.milestone.status_display}
                        related={related}
                        isShifted={bar.isShifted}
                        shiftReasonName={
                          bar.shiftReason
                            ? nameById.get(bar.shiftReason.sourceMilestoneId) || null
                            : null
                        }
                        rawStart={rawStart}
                        rawEnd={rawEnd}
                      />
                    }
                  >
                    <div
                      role="button"
                      tabIndex={0}
                      aria-label={`${bar.milestone.name} · ${bar.start.format("MMM D")} – ${bar.end.format("MMM D")}${bar.isShifted ? " (shifted)" : ""}`}
                      onClick={(e) => onToggleSelect(bar.milestone.id, e)}
                      className="absolute flex items-center justify-center text-white cursor-pointer"
                      style={
                        isSingleDay
                          ? {
                              left: bar.left + dayWidth / 2 - 7,
                              top: rowTop - 7,
                              width: 14,
                              height: 14,
                              borderRadius: 3,
                              transform: "rotate(45deg)",
                              backgroundColor: color,
                              opacity: isDimmed ? 0.35 : 1,
                              boxShadow: isSelected
                                ? `0 0 0 2px #fff, 0 0 0 4px ${TEAL}`
                                : bar.isShifted
                                  ? "0 0 0 1.5px #f59e0b"
                                  : "0 1px 2px rgba(16,24,32,0.08)",
                              zIndex: isSelected ? 3 : 2
                            }
                          : {
                              left: bar.left,
                              top: bar.rowIndex * sizes.rowHeight + (sizes.rowHeight - sizes.barHeight) / 2,
                              width: bar.width,
                              height: sizes.barHeight,
                              borderRadius: 6,
                              backgroundColor: color,
                              opacity: isDimmed ? 0.35 : 1,
                              boxShadow: isSelected
                                ? `0 0 0 2px #fff, 0 0 0 4px ${TEAL}`
                                : bar.isShifted
                                  ? "0 0 0 1.5px #f59e0b"
                                  : "0 1px 2px rgba(16,24,32,0.08)",
                              zIndex: isSelected ? 3 : 2
                            }
                      }
                    >
                      {!isSingleDay && isCompleted && <CheckOutlined style={{ fontSize: 11 }} />}
                    </div>
                  </Tooltip>
                  <span
                    className="absolute text-[11px] whitespace-nowrap flex items-center gap-1"
                    style={{
                      left: (isSingleDay ? bar.left + dayWidth : bar.left + bar.width) + 8,
                      top: bar.rowIndex * sizes.rowHeight + (sizes.rowHeight - sizes.barHeight) / 2,
                      height: sizes.barHeight,
                      lineHeight: `${sizes.barHeight}px`,
                      color: bar.isShifted ? "#b45309" : "#8c96a0",
                      opacity: isDimmed ? 0.35 : 1
                    }}
                  >
                    {bar.isShifted && <WarningFilled style={{ fontSize: 10 }} />}
                    {isSingleDay ? bar.start.format("MMM D") : formatWeekRange(bar.start, bar.end)}
                  </span>
                  {/* A separate, subtle marker for when the milestone ACTUALLY
                      finished — never a second bar, and never in place of the
                      planned one, which keeps representing the plan exactly
                      as scheduled regardless of how it played out. */}
                  {bar.actualEndOffset !== null && actualEndDate && (
                    <Tooltip title={`Actually finished ${actualEndDate.format("MMM D")}`}>
                      <div
                        aria-label={`${bar.milestone.name} actually finished ${actualEndDate.format("MMM D")}`}
                        className="absolute rounded-full"
                        style={{
                          left: bar.actualEndOffset - 4,
                          top:
                            bar.rowIndex * sizes.rowHeight +
                            (sizes.rowHeight - sizes.barHeight) / 2 +
                            sizes.barHeight -
                            4,
                          width: 8,
                          height: 8,
                          backgroundColor: "#1f2937",
                          border: "1.5px solid #fff",
                          boxShadow: "0 0 0 1px rgba(0,0,0,0.15)",
                          opacity: isDimmed ? 0.35 : 1,
                          zIndex: 4
                        }}
                      />
                    </Tooltip>
                  )}
                </React.Fragment>
              )
            })}

            {milestones.map((milestone, rowIndex) => {
              if (barById.has(milestone.id)) return null
              const hasDependency = !!dependencyCountByMilestone.get(milestone.id)
              return (
                <div
                  key={milestone.id}
                  className="absolute flex items-center gap-1 text-[11px]"
                  style={{
                    left: 8,
                    top: rowIndex * sizes.rowHeight,
                    height: sizes.rowHeight,
                    color: hasDependency ? "#d97706" : "#b0b7bd"
                  }}
                >
                  {hasDependency ? (
                    <>
                      <WarningFilled style={{ fontSize: 11 }} />
                      <span>Dates required to display timeline position</span>
                    </>
                  ) : (
                    <span>Dates not set</span>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

function MilestonesGanttLegend() {
  const { t } = useTranslation()

  return (
    <div className="flex items-center justify-between flex-wrap gap-2 mt-2 px-1">
      <div className="flex items-center gap-3 flex-wrap">
        {MILESTONE_STATUS_ORDER.map((status) => (
          <ActionTooltip
            key={status}
            title={t(MILESTONE_STATUSES_MAP[status].tooltipKey)}
          >
            <div className="flex items-center gap-1.5" tabIndex={0}>
              <span
                className="inline-block w-2 h-2 rounded-full"
                style={{ backgroundColor: MILESTONE_STATUSES_MAP[status]?.style?.color }}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t(MILESTONE_STATUSES_MAP[status].labelKey)}
              </Text>
            </div>
          </ActionTooltip>
        ))}
        <div className="flex items-center gap-1.5">
          <WarningFilled style={{ fontSize: 11, color: "#f59e0b" }} />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Shifted by a dependency
          </Text>
        </div>
      </div>
      <div className="flex items-center gap-1.5">
        <span style={{ color: "#94a3b8", fontSize: 13 }}>→</span>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Dependency
        </Text>
      </div>
    </div>
  )
}

function MilestoneBarTooltipContent({
  name,
  start,
  end,
  durationDays,
  statusLabel,
  related,
  isShifted,
  shiftReasonName,
  rawStart,
  rawEnd
}: {
  name: string
  start: Dayjs
  end: Dayjs
  durationDays: number
  statusLabel: string
  related: RelatedDependencyRow[]
  isShifted: boolean
  shiftReasonName: string | null
  rawStart: Dayjs | null
  rawEnd: Dayjs | null
}) {
  return (
    <div style={{ maxWidth: 260 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{name}</div>
      <div style={{ fontSize: 12, opacity: 0.85 }}>
        {start.format("MMM D")} – {end.format("MMM D")}
      </div>
      <div style={{ fontSize: 12, opacity: 0.85 }}>
        {durationDays} day{durationDays === 1 ? "" : "s"} · {statusLabel}
      </div>
      {isShifted && rawStart && rawEnd && (
        <div style={{ fontSize: 11.5, opacity: 0.75, marginTop: 4 }}>
          Originally planned {rawStart.format("MMM D")} – {rawEnd.format("MMM D")}
          {shiftReasonName ? ` — moved because ${shiftReasonName} finishes later.` : ""}
        </div>
      )}
      {related.length > 0 && (
        <div style={{ marginTop: 6, borderTop: "1px solid rgba(255,255,255,0.2)", paddingTop: 6 }}>
          {related.map(({ dependency, otherName, relation }) => (
            <div key={dependency.id} style={{ fontSize: 12, marginBottom: 2 }}>
              {relation}: {otherName}
              <div style={{ opacity: 0.75 }}>
                {TYPE_INFO[dependency.type].label}
                {dependency.lag_days ? ` · ${dependency.lag_days}d lag` : ""}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function MilestoneDetailPanel({
  milestone,
  related,
  effectiveEntry,
  nameById,
  parseDate
}: {
  milestone: ProjectMilestone
  related: RelatedDependencyRow[]
  effectiveEntry: EffectiveScheduleEntry | null
  nameById: Map<string, string>
  parseDate: (value: unknown) => Dayjs | null
}) {
  const rawStart = parseDate(milestone.start_date)
  const rawEnd = parseDate(milestone.end_date)
  const start = effectiveEntry?.start ?? rawStart
  const end = effectiveEntry?.end ?? rawEnd
  const durationDays = start && end ? end.diff(start, "day") + 1 : null

  return (
    <div
      data-testid="milestone-detail-panel"
      className="mt-2 p-3 rounded-lg"
      style={{ border: "1px solid #eef0f2", background: "#fafbfb" }}
    >
      <MilestoneDetailHeader name={milestone.name} statusLabel={milestone.status_display} />
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs" style={{ color: "#6b7280" }}>
        <span>
          {start && end ? `${start.format("MMM D")} – ${end.format("MMM D")}` : "Dates not set"}
        </span>
        {durationDays !== null && <span>{durationDays} days</span>}
      </div>
      {effectiveEntry?.isShifted && rawStart && rawEnd && (
        <div
          className="mt-2 flex items-start gap-1.5 text-xs px-2 py-1.5 rounded"
          style={{ background: "#fffbeb", color: "#92400e" }}
        >
          <WarningFilled style={{ fontSize: 12, marginTop: 1 }} />
          <span>
            Originally planned {rawStart.format("MMM D")} – {rawEnd.format("MMM D")}. Shifted
            because{" "}
            {effectiveEntry.shiftReason
              ? nameById.get(effectiveEntry.shiftReason.sourceMilestoneId) || "a dependency"
              : "a dependency"}{" "}
            finishes later.
          </span>
        </div>
      )}
      {related.length > 0 && (
        <div className="mt-2 flex flex-col gap-1">
          {related.map(({ dependency, otherName, relation }) => (
            <div key={dependency.id} className="text-xs flex items-center gap-1.5">
              <LinkOutlined style={{ color: TEAL, fontSize: 11 }} />
              <Text style={{ fontSize: 12 }}>
                {relation} <Text strong style={{ fontSize: 12 }}>{otherName}</Text> ·{" "}
                {TYPE_INFO[dependency.type].label}
                {dependency.lag_days ? ` · ${dependency.lag_days}d lag` : ""}
              </Text>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function MilestoneDetailHeader({ name, statusLabel }: { name: string; statusLabel: string }) {
  return (
    <div className="flex items-center gap-2">
      <Text strong style={{ fontSize: 13 }}>
        {name}
      </Text>
      <span
        className="text-[11px] px-1.5 py-0.5 rounded"
        style={{ background: "#eef0f2", color: "#6b7280" }}
      >
        {statusLabel}
      </span>
    </div>
  )
}
